// Extracted core content from ReceiptDetails - can be used in modal or inline
import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import RenderSelections from '../../../../../../Pedidos/Common/RenderSelections/RenderSelections';
import './ReceiptDetails.scss';
import { FaBan, FaPrint, FaMoneyBill1Wave, FaCoins, FaCreditCard, FaTag, FaListCheck, FaFileInvoice, FaClockRotateLeft, FaCircleExclamation, FaTriangleExclamation, FaUserGroup } from 'react-icons/fa6';
import { printReceiptById, updatePaymentReceiptTipPayed } from '../../../../../../../../crud/payments_api/payment_receipts';
import { getPaymentTransactionsByReceiptId } from '../../../../../../../../crud/payments_api/payment_transactions';
import { toast } from 'react-toastify';
import IndividualReceipt from '../../../../../../Pedidos/Common/CobrarPedidos/components/IndividualReceipt/IndividualReceipt';
import TransactionsView from './components/TransactionView/TransactionsView';
import { formatCurrency, getPermissionState } from '../../../../../../../../utils/functions';
import { PedidosProvider } from '../../../../../../Pedidos/PedidosContext';
import PostPaymentDiscount from './components/PostPaymentDiscount/PostPaymentDiscount';
import DteInfo from './components/DteInfo/DteInfo';
import CreateDte from './components/DteInfo/components/CreateDte/CreateDte';
import RefundManager from './components/RefundManager/RefundManager';
import { ActionHistory } from './components/ActionHistory';
import { CancelReceipt } from './components/CancelReceipt';

interface ReceiptDetailsContentProps {
  setReceipts?: (receipts: any[]) => void;
  currentReceipt: any;
  show?: boolean; // Optional - for modal behavior
  onClose?: () => void; // Optional - for modal behavior
  onReceiptSettled?: (receiptId: number) => void;
  readOnly?: boolean; // New prop - if true, hides payment/edit actions
  initialViewMode?: 'details' | 'payment' | 'transactions' | 'tip' | 'discount' | 'dte' | 'history';
}

const ReceiptDetailsContent: React.FC<ReceiptDetailsContentProps> = ({
  setReceipts,
  currentReceipt,
  show = true,
  onClose,
  onReceiptSettled,
  readOnly = false,
  initialViewMode = 'details'
}) => {
  const [viewMode, setViewMode] = useState<'details' | 'payment' | 'transactions' | 'tip' | 'discount' | 'dte' | 'history'>(initialViewMode);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDteCreateModal, setShowDteCreateModal] = useState(false);
  const [isPayed, setIsPayed] = useState(currentReceipt?.payed_full);
  const [allowRefund, setAllowRefund] = useState<boolean>(false);
  const [loadingRefundCheck, setLoadingRefundCheck] = useState<boolean>(false);
  const [hasApprovedCashPayment, setHasApprovedCashPayment] = useState<boolean>(false);
  const [hasUnapprovedCashPayment, setHasUnapprovedCashPayment] = useState<boolean>(false);
  const [tipAmount, setTipAmount] = useState<string>('');
  const [updatingTip, setUpdatingTip] = useState<boolean>(false);
  const [initialPaymentState, setInitialPaymentState] = useState(currentReceipt?.payed_full);
  const [pedidoUsers, setPedidoUsers] = useState<Array<{ user_id: number; user_name: string }>>([]);

  useEffect(() => {
    // Only close modal if payment status changed (and we have an onClose handler)
    if (isPayed && currentReceipt && isPayed !== initialPaymentState && onClose) {
      onReceiptSettled?.(currentReceipt.receipt_id);
      setViewMode('details');
      onClose();
    }
  }, [isPayed, onReceiptSettled, currentReceipt, onClose, initialPaymentState]);

  // Initialize tip amount when view mode changes to 'tip'
  useEffect(() => {
    if (viewMode === 'tip' && currentReceipt) {
      const currentTip = Math.round(currentReceipt?.tip_payed || 0);
      setTipAmount(currentTip.toString());
    }
  }, [viewMode, currentReceipt]);

  // Reset state when currentReceipt changes
  useEffect(() => {
    if (currentReceipt) {
      setIsPayed(currentReceipt.payed_full);
      setInitialPaymentState(currentReceipt.payed_full);

      // If cancelled, always start with details view (history also allowed)
      if (currentReceipt.is_cancelled) {
        setViewMode('details');
      } else if (readOnly) {
        // If read-only mode, always start with details
        setViewMode('details');
      } else if (!currentReceipt.payed_full) {
        setViewMode('payment');
      } else {
        setViewMode(initialViewMode);
      }

      setShowRefundModal(false);
    }
  }, [currentReceipt, readOnly, initialViewMode]);

  useEffect(() => {
    // Fetch transactions if receipt has any payment (partial or full) or is fully paid
    if ((currentReceipt?.total_payed > 0 || currentReceipt?.payed_full) && show) {
      setLoadingRefundCheck(true);
      getPaymentTransactionsByReceiptId(currentReceipt.receipt_id)
        .then((response) => {
          const REFUNDABLE_PAYMENT_TYPES = [
            'efectivo', 'cash', 'caja',
            'transferencia', 'transfer', 'banco', 'bank transfer'
          ];
          
          let hasApprovedCash = false;
          let hasUnapprovedCash = false;
          
          const hasRefundable = response.data.some((transaction: any) => {
            const isRefundableType = REFUNDABLE_PAYMENT_TYPES.some(type =>
              transaction.payment_type_name?.toLowerCase().includes(type)
            );
            
            if (!isRefundableType) return false;
            
            // For cash payments, check if approved
            const isCashPayment = ['efectivo', 'cash', 'caja'].some(type =>
              transaction.payment_type_name?.toLowerCase().includes(type)
            );
            
            if (isCashPayment) {
              const isApproved = transaction.approved === true;
              if (isApproved) {
                hasApprovedCash = true;
              } else {
                hasUnapprovedCash = true;
              }
              return isApproved;
            }
            
            return true;
          });
          
          // Extract unique users from transaction metadata
          const usersMap = new Map<number, { user_id: number; user_name: string }>();
          response.data.forEach((transaction: any) => {
            if (transaction.transaction_metadata?.pedido_users && Array.isArray(transaction.transaction_metadata.pedido_users)) {
              transaction.transaction_metadata.pedido_users.forEach((user: any) => {
                if (user.user_id && user.user_name && !usersMap.has(user.user_id)) {
                  usersMap.set(user.user_id, {
                    user_id: user.user_id,
                    user_name: user.user_name
                  });
                }
              });
            }
          });
          setPedidoUsers(Array.from(usersMap.values()));
          
          setHasApprovedCashPayment(hasApprovedCash);
          setHasUnapprovedCashPayment(hasUnapprovedCash);
          // Only allow refund if receipt is fully paid and not refunded
          if (currentReceipt?.payed_full && !currentReceipt?.is_refunded) {
            setAllowRefund(hasRefundable);
          } else {
            setAllowRefund(false);
          }
          setLoadingRefundCheck(false);
        })
        .catch(() => {
          setAllowRefund(false);
          setHasApprovedCashPayment(false);
          setHasUnapprovedCashPayment(false);
          setPedidoUsers([]);
          setLoadingRefundCheck(false);
        });
    } else {
      setAllowRefund(false);
      setHasApprovedCashPayment(false);
      setHasUnapprovedCashPayment(false);
      setPedidoUsers([]);
    }
  }, [currentReceipt, show]);

  if (!currentReceipt) {
    return null;
  }

  // Determine if receipt is cancelled - blocks most actions
  const isCancelled = currentReceipt?.is_cancelled === true;
  const isRefunded = currentReceipt?.is_refunded === true;

  const handlePrintReceipt = () => {
    printReceiptById(currentReceipt.receipt_id, null).then((response) => {
    }).catch((error) => {
      console.error('Error printing receipt: ', error);
      toast.error('Error al imprimir el recibo');
    });
  };

  const handleUpdateTip = async () => {
    if (updatingTip) return;

    const tipValue = parseInt(tipAmount) || 0;
    if (tipValue < 0) {
      toast.error('La propina no puede ser negativa');
      return;
    }

    setUpdatingTip(true);
    try {
      await updatePaymentReceiptTipPayed(currentReceipt.receipt_id, tipValue);
      toast.success('Propina actualizada correctamente');

      // Update the current receipt data
      currentReceipt.tip_payed = tipValue;

      // Optionally refresh the receipts list
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error('Error updating tip:', error);
      toast.error('Error al actualizar la propina');
    } finally {
      setUpdatingTip(false);
    }
  };

  const handleTipInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty string and integers only (no decimals for Chilean pesos)
    if (value === '' || /^\d+$/.test(value)) {
      setTipAmount(value);
    }
  };

  // Custom currency formatter for Chilean pesos (no decimals)
  const formatChileanPesos = (value: number) => {
    const roundedValue = Math.round(value);
    return `$${roundedValue.toLocaleString('es-CL', { maximumFractionDigits: 0 })}`;
  };

  const renderTipView = () => {
    const currentTipAmount = Math.round(currentReceipt?.tip_payed || 0);
    const suggestedTip = Math.round(currentReceipt?.suggested_tip || 0);
    const isButtonDisabled = updatingTip || tipAmount === currentTipAmount.toString() || readOnly;

    return (
      <div className="tip-update-container">
        {/* Current tip display */}
        <div className="tip-current-info">
          <div className="tip-info-card">
            <div className="tip-info-header">
              <FaCoins className="tip-icon" />
              <span className="tip-info-title">Propina actual</span>
            </div>
            <div className="tip-amount-display">
              {formatChileanPesos(currentTipAmount)}
            </div>
          </div>

          {suggestedTip > 0 && (
            <div className="tip-info-card suggested">
              <div className="tip-info-header">
                <FaCoins className="tip-icon" />
                <span className="tip-info-title">Propina sugerida</span>
              </div>
              <div className="tip-amount-display">
                {formatChileanPesos(suggestedTip)}
              </div>
            </div>
          )}
        </div>

        {/* Tip input section - hide if read-only */}
        {!readOnly && (
          <div className="tip-input-section">
            <div className="tip-input-header">
              <h4>Actualizar propina</h4>
              <p>Ingrese el nuevo monto de propina</p>
            </div>

            <div className="tip-input-wrapper">
              <div className="tip-input-group">
                <span className="currency-symbol">$</span>
                <input
                  type="text"
                  className="tip-input"
                  value={tipAmount}
                  onChange={handleTipInputChange}
                  placeholder="0"
                  disabled={updatingTip}
                />
              </div>
            </div>

            {/* Quick tip buttons */}
            <div className="tip-quick-buttons">
              <button
                className="tip-quick-btn"
                onClick={() => setTipAmount('0')}
                disabled={updatingTip}
              >
                Sin propina
              </button>
              {suggestedTip > 0 && (
                <button
                  className="tip-quick-btn"
                  onClick={() => setTipAmount(Math.round(suggestedTip).toString())}
                  disabled={updatingTip}
                >
                  Sugerida: {formatChileanPesos(suggestedTip)}
                </button>
              )}
              <button
                className="tip-quick-btn"
                onClick={() => setTipAmount(Math.round(currentReceipt?.total_due * 0.1).toString())}
                disabled={updatingTip}
              >
                10%
              </button>
            </div>

            {/* Update button */}
            <div className="tip-update-actions">
              <button
                className={`tip-update-btn ${updatingTip ? 'updating' : ''}`}
                onClick={handleUpdateTip}
                disabled={isButtonDisabled}
              >
                {updatingTip ? 'Actualizando...' : 'Actualizar propina'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderHeaderToggle = () => {
    // For cancelled receipts, only show Mesa (details) and Historial (history)
    if (isCancelled) {
      return (
        <div className="receipt-details-options-toggle-buttons">
          <Button
            variant='none'
            onClick={() => setViewMode('details')}
            className={viewMode === 'details' ? 'active' : 'inactive'}
          >
            <FaListCheck /> <span className="button-text">Mesa</span>
          </Button>

          <Button
            variant='none'
            onClick={() => setViewMode('history')}
            className={viewMode === 'history' ? 'active' : 'inactive'}
          >
            <FaClockRotateLeft /> <span className="button-text">Historial</span>
          </Button>
        </div>
      );
    }

    return (
      <div className="receipt-details-options-toggle-buttons">
        {(!currentReceipt?.payed_full && !isPayed && !readOnly) &&
          <Button
            variant='none'
            onClick={() => setViewMode('payment')}
            className={viewMode === 'payment' ? 'active' : 'inactive'}
          >
            <FaFileInvoice /> <span className="button-text">Pagar</span>
          </Button>
        }

        <Button
          variant='none'
          onClick={() => setViewMode('details')}
          className={viewMode === 'details' ? 'active' : 'inactive'}
        >
          <FaListCheck /> <span className="button-text">Mesa</span>
        </Button>

        {currentReceipt?.payed_full &&
          <Button
            variant='none'
            onClick={() => setViewMode('tip')}
            className={viewMode === 'tip' ? 'active' : 'inactive'}
          >
            <FaCoins /> <span className="button-text">Propina</span>
          </Button>
        }

        {currentReceipt?.payed_full && getPermissionState(131) && !readOnly &&
          <Button
            variant='none'
            onClick={() => setViewMode('discount')}
            className={viewMode === 'discount' ? 'active' : 'inactive'}
          >
            <FaTag /> <span className="button-text">Descuento</span>
          </Button>
        }

        {currentReceipt?.total_payed > 0 &&
          <Button
            variant='none'
            onClick={() => setViewMode('transactions')}
            className={viewMode === 'transactions' ? 'active' : 'inactive'}
          >
            <FaCreditCard /> <span className="button-text">Pagos</span>
          </Button>
        }

        {currentReceipt?.payed_full &&
          <Button
            variant='none'
            onClick={() => setViewMode('dte')}
            className={viewMode === 'dte' ? 'active' : 'inactive'}
          >
            <FaFileInvoice /> <span className="button-text">DTE</span>
          </Button>
        }

        {currentReceipt?.payed_full &&
          <Button
            variant='none'
            onClick={() => setViewMode('history')}
            className={viewMode === 'history' ? 'active' : 'inactive'}
          >
            <FaClockRotateLeft /> <span className="button-text">Historial</span>
          </Button>
        }
      </div>
    );
  };

  const handleRenderGeneralInfo = () => {
    return (
      <>
        <RenderSelections selections={currentReceipt ? currentReceipt.selections : []} showTotal={false} />

        {/* Users who attended the table */}
        {pedidoUsers.length > 0 && (
          <div className="receipt-attended-by">
            <div className="attended-by-header">
              <FaUserGroup className="attended-by-icon" />
              <span className="attended-by-label">Mesa atendida por:</span>
            </div>
            <div className="receipt-users-details-section">
              {(() => {
                // Identify table opener - usually the user who created the receipt
                const tableOpenerId = currentReceipt?.user_id;
                const tableOpener = pedidoUsers.find(user => user.user_id === tableOpenerId);
                const otherParticipants = pedidoUsers.filter(user => user.user_id !== tableOpenerId);
                
                return (
                  <>
                    {/* Table Opener */}
                    {tableOpener && (
                      <div className="receipt-user-role-group">
                        <div className="receipt-user-opener">
                          <span className="receipt-user-name-opener">{tableOpener.user_name}</span>
                          <span className="receipt-opener-badge">Apertura de mesa</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Other Participants */}
                    {otherParticipants.length > 0 && (
                      <div className="receipt-user-role-group">
                        <div className="receipt-participants-header">
                          <span className="receipt-participants-label">Participantes:</span>
                        </div>
                        <div className="receipt-participants-list">
                          {otherParticipants.map((user, index) => (
                            <span key={user.user_id} className="receipt-user-participant">
                              {user.user_name}{index < otherParticipants.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Fallback - if no opener identified, show all as participants */}
                    {!tableOpener && (
                      <div className="receipt-user-role-group">
                        <div className="receipt-participants-list">
                          {pedidoUsers.map((user, index) => (
                            <span key={user.user_id} className="receipt-user-participant">
                              {user.user_name}{index < pedidoUsers.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()
              }
            </div>
          </div>
        )}

        {/* Modern minimalist receipt summary */}
        <div className="receipt-details-summary">
          {currentReceipt?.coupon_amount > 0 && (
            <div className="summary-item">
              <span className="label">Cupón</span>
              <span className="amount">{formatCurrency(currentReceipt.coupon_amount)}</span>
            </div>
          )}
          {currentReceipt?.suggested_tip > 0 && (
            <div className="summary-item">
              <span className="label">Propina sugerida</span>
              <span className="amount">{formatCurrency(currentReceipt.suggested_tip)}</span>
            </div>
          )}
          {currentReceipt?.discount_amount > 0 && (
            <div className="summary-item">
              <span className="label">Descuento</span>
              <span className="amount">{formatCurrency(currentReceipt.discount_amount)}</span>
            </div>
          )}
          {currentReceipt?.subtotal > 0 && (
            <div className="summary-item">
              <span className="label">Subtotal</span>
              <span className="amount">{formatCurrency(currentReceipt.subtotal)}</span>
            </div>
          )}

          <div className='receipt-details-total'>
            <span className="label">Total pagado</span>
            <span className="amount">{formatCurrency(currentReceipt.total_payed)}</span>
          </div>
        </div>

        {/* Action buttons - hide if read-only */}
        {!readOnly && (
          <div className="receipt-details-action-section">
            {getPermissionState(131) && !currentReceipt?.is_cancelled && !hasApprovedCashPayment && (
              <button className="action-button cancel-button" onClick={() => setShowCancelModal(true)}>
                <FaBan /> Cancelar
              </button>
            )}
            {/* Solo mostrar botón de devolución si NO hay pagos en efectivo sin aprobar */}
            {currentReceipt?.payed_full && !currentReceipt?.is_refunded && allowRefund && !loadingRefundCheck && !hasUnapprovedCashPayment && (
              <button className="action-button refund-button" onClick={() => setShowRefundModal(true)}>
                <FaMoneyBill1Wave /> Procesar devolución
              </button>
            )}
            <button className="action-button print-button" onClick={handlePrintReceipt}>
              <FaPrint /> Re-Imprimir
            </button>
          </div>
        )}

        {/* Read-only mode - show only print button */}
        {readOnly && (
          <div className="receipt-details-action-section">
            <button className="action-button print-button" onClick={handlePrintReceipt}>
              <FaPrint /> Imprimir Recibo
            </button>
          </div>
        )}
      </>
    );
  };

  const handlePaymentInfo = () => {
    if (readOnly) {
      // In read-only mode, show details instead of payment form
      return handleRenderGeneralInfo();
    }

    return (
      <PedidosProvider>
        <div className="receipt-container">
          <IndividualReceipt
            receipt={currentReceipt}
            receipts={[currentReceipt]}
            setReceipts={setReceipts || (() => { })}
            setCurrentIndex={() => { }} // Dummy function since we don't need index management here
            setIsPayed={setIsPayed}
            onGenerateDte={handleGenerateDte}
            showDteQuickAction={currentReceipt?.payed_full && !currentReceipt?.is_cancelled}
          />
        </div>
      </PedidosProvider>
    );
  };

  // DTE Generation handlers
  const handleGenerateDte = () => {
    setShowDteCreateModal(true);
  };

  const handleDteCreated = () => {
    setShowDteCreateModal(false);
    setViewMode('dte');
    toast.success('DTE generado exitosamente');
  };

  const handleDiscountApplied = (newDiscountAmount: number) => {
    // Update the current receipt with the new discount
    if (setReceipts) {
      const updatedReceipts = [currentReceipt].map((r: any) => {
        if (r.receipt_id === currentReceipt.receipt_id) {
          return {
            ...r,
            discount_amount: newDiscountAmount,
          };
        }
        return r;
      });
      setReceipts(updatedReceipts);
    }

    // Refresh to show updated data
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  return (
    <>
      {/* Refunded or cancelled receipt banner (refund takes precedence) */}
      {isRefunded ? (
        <div className="receipt-refunded-banner">
          <FaTriangleExclamation className="banner-icon" />
          <div className="banner-content">
            <span className="banner-title">Recibo Reembolsado</span>
            <span className="banner-subtitle">Este recibo ha sido reembolsado</span>
          </div>
        </div>
      ) : isCancelled ? (
        <div className="receipt-cancelled-banner">
          <FaCircleExclamation className="banner-icon" />
          <div className="banner-content">
            <span className="banner-title">Recibo Cancelado</span>
            <span className="banner-subtitle">Este recibo ha sido cancelado y no puede ser modificado</span>
          </div>
        </div>
      ) : null}

      {renderHeaderToggle()}
      {viewMode === 'details' && handleRenderGeneralInfo()}
      {viewMode === 'payment' && !isCancelled && handlePaymentInfo()}
      {viewMode === 'tip' && !isCancelled && renderTipView()}
      {viewMode === 'discount' && !readOnly && !isCancelled && (
        <PostPaymentDiscount
          receiptId={currentReceipt.receipt_id}
          totalPayed={currentReceipt.total_payed || currentReceipt.total_due}
          currentDiscount={currentReceipt.discount_amount || 0}
          onDiscountApplied={handleDiscountApplied}
          onClose={() => setViewMode('details')}
        />
      )}
      {viewMode === 'transactions' && !isCancelled && <TransactionsView currentReceipt={currentReceipt} />}
      {viewMode === 'dte' && !isCancelled && <DteInfo receiptId={currentReceipt.receipt_id} localId={currentReceipt.local_id} />}
      {viewMode === 'history' && <ActionHistory receiptId={currentReceipt.receipt_id} />}

      {/* Refund manager - only show if not read-only and not cancelled */}
      {!readOnly && !isCancelled && (
        <RefundManager
          receipt={currentReceipt}
          show={showRefundModal}
          onClose={() => setShowRefundModal(false)}
        />
      )}

      {/* Cancel receipt modal - only show if not read-only and not already cancelled */}
      {!readOnly && !isCancelled && (
        <CancelReceipt
          show={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          receiptId={currentReceipt.receipt_id}
        />
      )}

      {/* CreateDte Modal - Quick access from IndividualReceipt */}
      {currentReceipt?.payed_full && !isCancelled && (
        <CreateDte
          show={showDteCreateModal}
          receiptId={currentReceipt.receipt_id}
          localId={currentReceipt.local_id}
          onSuccess={handleDteCreated}
          onCancel={() => setShowDteCreateModal(false)}
        />
      )}
    </>
  );
};

export default ReceiptDetailsContent;
